import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';

import { unGroupWidget } from 'actions/group';

import { EditorContext } from '../../index';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import { Property } from '../../components/properties/';
import { Container } from '../core/container';
import { useChildrenWidgetSelected, useWidgetSelected } from 'components/Editor/hooks/useEditorSession';
import { CONTENT_CARD } from 'constants/widgetTypes';
import { splitPageBySelection, toggleEditMode, updateWidgetActions} from 'actions/editor';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';
import Actions from 'components/Editor/components/actions';

import styles from './group.module.scss';
import { useMakeFrame } from 'components/Editor/hooks/useMakeFrame';


const GroupWidgetComponent = function ({ widgetProps, widgetRef, className, style, children }) {
  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const selected = useWidgetSelected(editor, id);
  const childrenSelection = useChildrenWidgetSelected(editor, id);

  var widgetStyle = {
    ...style
  }

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div id={id}
        ref={ref}
        className={cx(className, styles.groupWidget, 'cs_group_widget', {
          [styles.mobile]: breakpoint === 'mobile',
          [styles.partialSelection]: childrenSelection.selected
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-container={false}
        data-selectable={true}
        style={widgetStyle}
      >
        {!(selected || childrenSelection.selected) &&
          <div className={styles.overlay} onClick={(e) => {
            e.stopPropagation();
          }}></div>
        }
        <Animation widget={widgetProps}>
        <div className={styles.groupWidgetAnimation}>
             {children}
        </div>
        </Animation>
      
        

      </div>
    </Container>
  )
}

const groupWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const GroupWidget = React.memo(GroupWidgetComponent, groupWidgetEquals);

const GroupWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const { makeFrameShow, makeFramePageId } = useMakeFrame({ editorSession });

  const handleUngroup = () => {
    dispatch(unGroupWidget(editorSession, id));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false, view));
    setView(view);
  }

  const handleActionChange = (action) => {
    const actions = action ? [action] : [];
    dispatch(updateWidgetActions(editorSession, props.uid, actions));
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }
    
  const handleMakeFrame = () => {
    dispatch(splitPageBySelection(editorSession, makeFramePageId, [props.uid]));
  };

  return (
    <>
      <Property.Action className={styles.textButton} onClick={handleUngroup}>Ungroup</Property.Action>
      {makeFrameShow && props?.breakpoint === 'desktop' && 
        <>
          <Property.Action className={styles.textButton} tooltip='Split segment: selection becomes a new frame' onClick={handleMakeFrame}>
            Make Frame
          </Property.Action>
          <Property.Divider />
        </>
      }
      <Property.Action className={styles.iconButton} tooltip="Insert Link" onClick={() => changeView('action')}>
        <i className={cx("cleverstory-link")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
        <i className={cx("cleverstory-animations")} />
        </Property.Action>
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'SECTIONS'} widgets={[props]} onCancel={() => closePopup()} />
      }
      {!editMode && view === 'action' &&
        <Actions
          target={id}
          editorSession={editorSession}
          value={_.get(props, 'actions.0')}
          onChange={handleActionChange}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  )
}

GroupWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: false,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    renderDirections: [],
    onReparent: (widget, parent) => {
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === CONTENT_CARD) {
        return false;
      }
      return true;
    }
  }
}

GroupWidget.actions = (editor, breakpoint, props) => {
  return (
    <GroupWidgetDesktopAction
      editor={editor}
      breakpoint={breakpoint}
      {...props}
    />
  );
}

GroupWidget.processRequest = (widget) => {
  const actions = _.get(widget, 'actions');
  
  const updated = (actions || []).map((action)=> {
    var value = _.get(action, 'value.content.id') || _.get(action, 'value', null);

    if(value != null) {
      if(action.type == 'asset') {
        return {
          ...action,
          value: value
        }
      }
      
      return action;
    }
  })

  return {
    ...widget,
    actions: updated
  }
}

export { GroupWidget }